import type { Notification } from '../types/notifications';

/** Narrow an unknown metadata value to a non-empty string, else null. */
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

type Resolver = (n: Notification) => string | null;

/**
 * Shared shape for notifications that can point at either a Red Dot
 * workflow instance or a task: prefer the workflow route, else fall back
 * to the task route, else null.
 */
const resolveWorkflowOrTask: Resolver = (n) => {
  const workflowInstanceId = str(n.metadata?.workflowInstanceId);
  if (workflowInstanceId) return `/red-dot/${workflowInstanceId}`;
  const taskId = str(n.metadata?.taskId) ?? n.taskId;
  return taskId ? `/my-tasks?taskId=${taskId}` : null;
};

/**
 * Maps a notification `subtype` to the in-app route a click should open.
 * Each resolver builds its target from the ids the backend persists
 * (`metadata` / top-level `taskId`). Add an entry here to make a new
 * notification type clickable — no other frontend change is needed.
 */
const RESOLVERS: Record<string, Resolver> = {
  RED_DOT_SUBMITTED: (n) => {
    const id = str(n.metadata?.workflowInstanceId);
    return id ? `/red-dot/${id}` : null;
  },
  COMMENT_ADDED: resolveWorkflowOrTask,
  // ADO 477 — mentioned users get their own notification subtype, same target as COMMENT_ADDED.
  COMMENT_MENTION: resolveWorkflowOrTask,
  TASK_WATCHER_ADDED: (n) => (n.taskId ? `/my-tasks?taskId=${n.taskId}` : null),
  TASK_COMPLETED: resolveWorkflowOrTask,
  // ADO 452 — sent to the incoming owner on a Red Dot ownership transfer.
  RED_DOT_REASSIGNED: (n) => {
    const id = str(n.metadata?.workflowInstanceId);
    return id ? `/red-dot/${id}` : null;
  },
  // ADO 452 (Step 8) — sent to the outgoing owner, only when still active.
  RED_DOT_REASSIGNED_AWAY: (n) => {
    const id = str(n.metadata?.workflowInstanceId);
    return id ? `/red-dot/${id}` : null;
  },
};

/**
 * Resolve the deep-link a notification click should navigate to.
 * Registry first, then any legacy `linkUrl` the backend stored (keeps
 * historical notifications working). Returns null when not navigable.
 */
export function resolveNotificationLink(n: Notification): string | null {
  const fromRegistry = n.subtype ? RESOLVERS[n.subtype]?.(n) : null;
  return fromRegistry ?? n.linkUrl ?? null;
}
